import { getGuestImageGenerationLimitState } from './guestImageGenerationLimit';
import { refreshAccountCredits } from './accountCredits';
import {
  createUserSessionHeaders,
  fetchAwsApi,
  getApiErrorMessage,
  isApiResponseOk,
  listStorageKeys,
  parseApiResponse,
  readStorageValue,
  removeStorageValue,
  sanitizeGenericText,
  sanitizeImageUrl,
  writeStorageValue,
} from '../security';

export interface LambdaTryOnSourceSelection {
  inventoryIds?: string[];
  slot?: string | null;
}

export interface SubmitLambdaTryOnAddItemInput {
  rawDataId: string;
  sourceSelections?: LambdaTryOnSourceSelection[];
  fallbackInventoryIds?: string[];
  activeInventoryIds?: string[];
  primarySelectionSlot?: string | null;
  primaryInventoryId?: string | null;
  confirmGeneration?: boolean;
  idempotencyKey?: string;
  turnstileRequired?: boolean;
  turnstileToken?: string;
}

export interface SubmitLambdaTryOnAddItemResult {
  rawDataId: string;
  resultId: string;
  generationRequestId: string;
  status: string;
  imageUrl: string;
  inventoryId: string | null;
  inventoryIds: string[];
  idempotencyKey: string;
  requiresGeneration: boolean;
  requiresConfirmation: boolean;
  creditCost: number | null;
}

export interface SubmitLambdaTryOnRemoveItemInput {
  inventoryId: string;
}

export interface LambdaTryOnResultRecord {
  id: string;
  generationRequestId?: string;
  status: string;
  imageUrl: string;
  freshImageUrl?: string;
  objectKey?: string;
  inventoryIds: string[];
  errorMessage: string;
  isSaved: boolean;
  createdAt?: string;
  completedAt?: string;
}

export interface FetchLambdaTryOnResultsResult {
  rawDataId: string;
  results: LambdaTryOnResultRecord[];
}

export interface LambdaTryOnSelectionRecord {
  id: string;
  itemId: string;
  inventoryId: string;
  tryOnCategory: string;
  itemCategory: string;
  colorCode: string;
  color: string;
  productImageS3Key: string;
  title: unknown;
  price: number;
}

export async function fetchLambdaTryOnSelections({ signal }: { signal?: AbortSignal } = {}) {
  const headers = createTryOnRequestHeaders();
  const queryParams = new URLSearchParams({ store: getTryOnStoreName() });
  if (isGuestTryOnRequest(headers)) {
    queryParams.set('account_id', 'null');
  }
  const response = await fetchAwsApi(`/api/try_on_selections?${queryParams}`, {
    method: 'GET',
    headers,
    signal,
  });
  const body = await parseApiResponse(response) as Record<string, unknown>;
  if (!isApiResponseOk(response, body)) {
    throw new Error(getApiErrorMessage(body, 'Unable to load try-on selections.'));
  }

  const rawDataId = sanitizeUuid(body.raw_data_id);
  const selections = (Array.isArray(body.selections) ? body.selections : [])
    .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object' && !Array.isArray(row)))
    .map((row): LambdaTryOnSelectionRecord => ({
      id: sanitizeUuid(row.id),
      itemId: sanitizeUuid(row.item_id),
      inventoryId: sanitizeUuid(row.inventory_id),
      tryOnCategory: sanitizeGenericText(String(row.try_on_category ?? ''), 40),
      itemCategory: sanitizeGenericText(String(row.item_category ?? ''), 40),
      colorCode: sanitizeGenericText(String(row.color_code ?? ''), 80),
      color: sanitizeGenericText(String(row.color ?? ''), 80),
      productImageS3Key: sanitizeGenericText(String(row.product_image_s3_key ?? ''), 512),
      title: row.title,
      price: Number(row.price) || 0,
    }))
    .filter((row) => row.itemId && row.inventoryId)
    .sort((left, right) => left.inventoryId.localeCompare(right.inventoryId));

  return { rawDataId, selections };
}

export interface SaveLambdaTryOnResultResult {
  id: string;
  rawDataId: string;
  s3Key: string;
  isSaved: boolean;
  savedAt: string;
}

const lambdaTryOnResultsCache = new Map<string, FetchLambdaTryOnResultsResult>();
const lambdaTryOnResultsCacheUpdatedAt = new Map<string, number>();
const lambdaTryOnResultsCacheVersions = new Map<string, number>();
const lambdaTryOnResultsRequestCache = new Map<string, Promise<FetchLambdaTryOnResultsResult>>();
const lambdaTryOnImageUrlCache = new Map<string, Promise<string>>();
const lambdaTryOnStableDisplayUrlCache = new Map<string, string>();
const LAMBDA_TRY_ON_IMAGE_DATA_STORAGE_KEY_PREFIX = 'vadauro_try_on_image_data:';

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function sanitizeUuid(value: unknown) {
  const sanitizedValue = sanitizeGenericText(String(value ?? ''), 80);
  return isUuid(sanitizedValue) ? sanitizedValue : '';
}

function sanitizeInventoryIds(value: unknown) {
  const inventoryIdCandidates: unknown[] = [];

  const collectInventoryIdCandidates = (candidate: unknown, depth = 0) => {
    if (candidate === undefined || candidate === null || candidate === '') {
      return;
    }

    if (Array.isArray(candidate)) {
      candidate.forEach((entry) => collectInventoryIdCandidates(entry, depth + 1));
      return;
    }

    if (typeof candidate === 'object') {
      if (depth >= 4) {
        return;
      }
      Object.values(candidate as Record<string, unknown>).forEach((entry) => {
        collectInventoryIdCandidates(entry, depth + 1);
      });
      return;
    }

    const stringValue = sanitizeGenericText(String(candidate), 2048).trim();
    if (!stringValue) {
      return;
    }
    if (isUuid(stringValue)) {
      inventoryIdCandidates.push(stringValue);
      return;
    }

    if (depth < 4 && (
      (stringValue.startsWith('[') && stringValue.endsWith(']')) ||
      (stringValue.startsWith('{') && stringValue.endsWith('}'))
    )) {
      try {
        collectInventoryIdCandidates(JSON.parse(stringValue), depth + 1);
        return;
      } catch {
        const unwrappedValue = stringValue.slice(1, -1);
        if (unwrappedValue.includes(',')) {
          unwrappedValue.split(',').forEach((entry) => {
            collectInventoryIdCandidates(entry.trim().replace(/^['"]|['"]$/g, ''), depth + 1);
          });
          return;
        }
      }
    }

    if (depth < 4 && /[,|]/.test(stringValue)) {
      stringValue.split(/[,|]/).forEach((entry) => {
        collectInventoryIdCandidates(entry.trim(), depth + 1);
      });
    }
  };

  collectInventoryIdCandidates(value);

  return Array.from(
    new Set(
      inventoryIdCandidates
        .map((entry) => sanitizeUuid(entry))
        .filter(Boolean)
    )
  );
}

export function collectActiveTryOnInventoryIds(
  items: Array<{ inventoryIds?: string[] }>
) {
  return sanitizeInventoryIds(items.flatMap((item) => item.inventoryIds ?? []))
    .sort((leftInventoryId, rightInventoryId) => leftInventoryId.localeCompare(rightInventoryId));
}

function sanitizeTryOnResultInventoryIds(candidate: Record<string, unknown>) {
  return sanitizeInventoryIds([
    candidate.inventory_ids,
    candidate.inventoryIds,
    candidate.selected_inventory_ids_by_category,
    candidate.selectedInventoryIdsByCategory,
    candidate.inventory_ids_by_category,
    candidate.inventoryIdsByCategory,
    candidate.inventory_id,
    candidate.inventoryId,
    candidate.upper_inventory_id,
    candidate.upperInventoryId,
    candidate.upper_body_inventory_id,
    candidate.upperBodyInventoryId,
    candidate.top_inventory_id,
    candidate.topInventoryId,
    candidate.lower_inventory_id,
    candidate.lowerInventoryId,
    candidate.lower_body_inventory_id,
    candidate.lowerBodyInventoryId,
    candidate.bottom_inventory_id,
    candidate.bottomInventoryId,
    candidate.other_inventory_id,
    candidate.otherInventoryId,
    candidate.accessory_inventory_id,
    candidate.accessoryInventoryId,
    candidate.shoes_inventory_id,
    candidate.shoesInventoryId,
  ]);
}

function sanitizeTryOnStatus(value: unknown) {
  return sanitizeGenericText(String(value ?? ''), 40).toLowerCase();
}

function sanitizeTryOnResultId(candidate: Record<string, unknown>) {
  return sanitizeGenericText(
    String(
      candidate.id
      ?? candidate.result_id
      ?? candidate.resultId
      ?? candidate.try_on_result_id
      ?? candidate.tryOnResultId
      ?? candidate.try_on_results_id
      ?? candidate.tryOnResultsId
      ?? ''
    ),
    120
  );
}

function sanitizeTryOnResultStatus(candidate: Record<string, unknown>) {
  return sanitizeTryOnStatus(
    candidate.status ?? candidate.generation_status ?? candidate.generationStatus
  );
}

function getTryOnResultImageUrl(candidate: Record<string, unknown>) {
  return candidate.image_url
    ?? candidate.imageUrl
    ?? candidate.output_image_url
    ?? candidate.outputImageUrl
    ?? candidate.result_image_url
    ?? candidate.resultImageUrl;
}

function sanitizeBoolean(value: unknown) {
  return value === true || String(value ?? '').toLowerCase() === 'true';
}

function sanitizeCreditCost(value: unknown) {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  const parsedValue = Number.parseFloat(String(value ?? '').trim());
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
}

function sanitizeTryOnImageUrl(value: unknown) {
  return sanitizeImageUrl(value, true, true);
}

function getLambdaTryOnImageDataStorageKey(cacheKey: string) {
  return `${LAMBDA_TRY_ON_IMAGE_DATA_STORAGE_KEY_PREFIX}${encodeURIComponent(cacheKey)}`;
}

function getLambdaTryOnImageCacheKey(imageUrl: string, cacheKeyInput?: unknown) {
  const explicitCacheKey = sanitizeGenericText(String(cacheKeyInput ?? ''), 240);
  if (explicitCacheKey) {
    return explicitCacheKey;
  }

  return `image-url:${imageUrl}`;
}

function getLambdaTryOnInventoryImageCacheKey(rawDataId: string, inventoryIds: string[]) {
  const inventoryKey = buildInventoryIdSetKey(inventoryIds);
  return rawDataId && inventoryKey ? `raw:${rawDataId}:inventory:${inventoryKey}` : '';
}

function getLambdaTryOnImageCacheKeys(
  imageUrl: string,
  cacheKeyInput?: unknown,
  additionalCacheKeyInputs: unknown[] = []
) {
  const cacheKeys = Array.from(
    new Set(
      [cacheKeyInput, ...additionalCacheKeyInputs]
        .map((keyInput) => getLambdaTryOnImageCacheKey(imageUrl, keyInput))
        .filter(Boolean)
    )
  );
  const fallbackImageUrlCacheKey = imageUrl.startsWith('data:')
    ? ''
    : getLambdaTryOnImageCacheKey(imageUrl);
  if (fallbackImageUrlCacheKey && !cacheKeys.includes(fallbackImageUrlCacheKey)) {
    cacheKeys.push(fallbackImageUrlCacheKey);
  }

  return cacheKeys;
}

function sanitizeTryOnGenerationRequestId(candidate: Record<string, unknown>) {
  return sanitizeGenericText(
    String(candidate.generation_request_id ?? candidate.generationRequestId ?? ''),
    160
  );
}

const LAMBDA_TRY_ON_PRESIGNED_URL_REFRESH_BUFFER_MS = 30_000;

function getAwsPresignedUrlExpiration(imageUrl: string) {
  try {
    const parsedUrl = new URL(imageUrl);
    const signedAt = parsedUrl.searchParams.get('X-Amz-Date');
    const expiresInSeconds = Number.parseInt(
      parsedUrl.searchParams.get('X-Amz-Expires') ?? '',
      10
    );
    const match = signedAt?.match(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/
    );
    if (!match || !Number.isFinite(expiresInSeconds) || expiresInSeconds < 0) {
      return null;
    }

    const signedAtMs = Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6])
    );
    return Number.isFinite(signedAtMs)
      ? signedAtMs + expiresInSeconds * 1000
      : null;
  } catch {
    return null;
  }
}

function shouldReplaceStableTryOnImageUrl(stableImageUrl: string, freshImageUrl: string) {
  if (!stableImageUrl || stableImageUrl === freshImageUrl || stableImageUrl.startsWith('data:')) {
    return false;
  }

  const stableUrlExpiration = getAwsPresignedUrlExpiration(stableImageUrl);
  return stableUrlExpiration !== null &&
    stableUrlExpiration <= Date.now() + LAMBDA_TRY_ON_PRESIGNED_URL_REFRESH_BUFFER_MS;
}

function invalidateLambdaTryOnResultsCache(rawDataId: string) {
  const cacheKeys = new Set([
    ...Array.from(lambdaTryOnResultsCache.keys()),
    ...Array.from(lambdaTryOnResultsRequestCache.keys()),
  ]);
  Array.from(cacheKeys)
    .filter((cacheKey) => cacheKey === rawDataId || cacheKey.startsWith(`${rawDataId}:`))
    .forEach((cacheKey) => {
      lambdaTryOnResultsCache.delete(cacheKey);
      lambdaTryOnResultsCacheUpdatedAt.delete(cacheKey);
      lambdaTryOnResultsRequestCache.delete(cacheKey);
    });
  lambdaTryOnResultsCacheVersions.set(
    rawDataId,
    (lambdaTryOnResultsCacheVersions.get(rawDataId) ?? 0) + 1
  );
}

async function convertBlobToDataUrl(blob: Blob) {
  return await new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onerror = () => reject(fileReader.error ?? new Error('Unable to read image data.'));
    fileReader.onload = () => resolve(typeof fileReader.result === 'string' ? fileReader.result : '');
    fileReader.readAsDataURL(blob);
  });
}

// Cached data URLs run to hundreds of KB each; without a budget they fill the
// whole localStorage quota and every later write on the origin silently fails.
const LAMBDA_TRY_ON_IMAGE_DATA_TOTAL_BUDGET = 2_000_000;
const LAMBDA_TRY_ON_IMAGE_DATA_MAX_ENTRY = 900_000;
const LAMBDA_TRY_ON_IMAGE_DATA_VERSION_PREFIX = 'v1|';
const LAMBDA_TRY_ON_IMAGE_DATA_REF_PREFIX = 'ref:';

function decodeLambdaTryOnImageDataEntry(rawValue: string | null) {
  if (!rawValue) {
    return null;
  }

  if (!rawValue.startsWith(LAMBDA_TRY_ON_IMAGE_DATA_VERSION_PREFIX)) {
    // Legacy entry without a timestamp; treated as oldest when pruning.
    return { timestamp: 0, payload: rawValue };
  }

  const payloadSeparatorIndex = rawValue.indexOf('|', LAMBDA_TRY_ON_IMAGE_DATA_VERSION_PREFIX.length);
  if (payloadSeparatorIndex === -1) {
    return null;
  }

  const timestamp = Number(rawValue.slice(LAMBDA_TRY_ON_IMAGE_DATA_VERSION_PREFIX.length, payloadSeparatorIndex));
  return {
    timestamp: Number.isFinite(timestamp) ? timestamp : 0,
    payload: rawValue.slice(payloadSeparatorIndex + 1),
  };
}

function encodeLambdaTryOnImageDataEntry(payload: string) {
  return `${LAMBDA_TRY_ON_IMAGE_DATA_VERSION_PREFIX}${Date.now()}|${payload}`;
}

function pruneLambdaTryOnImageDataCache(incomingSize = 0) {
  const storedKeys = listStorageKeys(LAMBDA_TRY_ON_IMAGE_DATA_STORAGE_KEY_PREFIX, { storage: 'local' });
  if (storedKeys.length === 0) {
    return;
  }

  const entries = storedKeys
    .map((storageKey) => {
      const rawValue = readStorageValue(storageKey, { storage: 'local' }) ?? '';
      return {
        storageKey,
        timestamp: decodeLambdaTryOnImageDataEntry(rawValue)?.timestamp ?? 0,
        size: storageKey.length + rawValue.length,
      };
    })
    .sort((leftEntry, rightEntry) => leftEntry.timestamp - rightEntry.timestamp);

  let totalSize = entries.reduce((accumulator, entry) => accumulator + entry.size, 0);
  for (const entry of entries) {
    if (totalSize + incomingSize <= LAMBDA_TRY_ON_IMAGE_DATA_TOTAL_BUDGET) {
      break;
    }

    removeStorageValue(entry.storageKey, { storage: 'local' });
    totalSize -= entry.size;
  }
}

// Trim any oversized cache left behind by earlier sessions as soon as the
// module loads — a full cache blocks unrelated writes across the whole origin.
pruneLambdaTryOnImageDataCache();

function readCachedLambdaTryOnImageData(cacheKey: string) {
  const cachedEntry = decodeLambdaTryOnImageDataEntry(
    readStorageValue(getLambdaTryOnImageDataStorageKey(cacheKey), { storage: 'local' })
  );
  if (!cachedEntry) {
    return '';
  }

  let payload = cachedEntry.payload;
  if (payload.startsWith(LAMBDA_TRY_ON_IMAGE_DATA_REF_PREFIX)) {
    const referencedEntry = decodeLambdaTryOnImageDataEntry(
      readStorageValue(payload.slice(LAMBDA_TRY_ON_IMAGE_DATA_REF_PREFIX.length), { storage: 'local' })
    );
    payload = referencedEntry && !referencedEntry.payload.startsWith(LAMBDA_TRY_ON_IMAGE_DATA_REF_PREFIX)
      ? referencedEntry.payload
      : '';
  }

  return sanitizeTryOnImageUrl(payload);
}

function persistLambdaTryOnImageData(cacheKeys: string[], imageDataUrl: string) {
  if (
    cacheKeys.length <= 0 ||
    !imageDataUrl.startsWith('data:image/') ||
    imageDataUrl.length > LAMBDA_TRY_ON_IMAGE_DATA_MAX_ENTRY
  ) {
    return;
  }

  pruneLambdaTryOnImageDataCache(imageDataUrl.length);
  const [primaryCacheKey, ...aliasCacheKeys] = cacheKeys;
  const primaryStorageKey = getLambdaTryOnImageDataStorageKey(primaryCacheKey);
  const didWritePrimary = writeStorageValue(
    primaryStorageKey,
    encodeLambdaTryOnImageDataEntry(imageDataUrl),
    { storage: 'local' }
  );
  if (!didWritePrimary) {
    return;
  }

  aliasCacheKeys.forEach((aliasCacheKey) => {
    writeStorageValue(
      getLambdaTryOnImageDataStorageKey(aliasCacheKey),
      encodeLambdaTryOnImageDataEntry(`${LAMBDA_TRY_ON_IMAGE_DATA_REF_PREFIX}${primaryStorageKey}`),
      { storage: 'local' }
    );
  });
}

async function cacheLambdaTryOnImageUrl(
  imageUrlInput: unknown,
  cacheKeyInput?: unknown,
  additionalCacheKeyInputs: unknown[] = []
) {
  const imageUrl = sanitizeTryOnImageUrl(imageUrlInput);
  if (!imageUrl || imageUrl.startsWith('blob:')) {
    return imageUrl;
  }

  const cacheKeys = getLambdaTryOnImageCacheKeys(
    imageUrl,
    cacheKeyInput,
    additionalCacheKeyInputs
  );
  if (imageUrl.startsWith('data:image/')) {
    persistLambdaTryOnImageData(cacheKeys, imageUrl);
    const resolvedImageData = Promise.resolve(imageUrl);
    cacheKeys.forEach((candidateCacheKey) => {
      lambdaTryOnImageUrlCache.set(candidateCacheKey, resolvedImageData);
    });
    return imageUrl;
  }

  const cachedImageDataUrl =
    cacheKeys.map((candidateCacheKey) => readCachedLambdaTryOnImageData(candidateCacheKey)).find(Boolean) ?? '';
  if (cachedImageDataUrl) {
    const resolvedCacheEntry = Promise.resolve(cachedImageDataUrl);
    cacheKeys.forEach((candidateCacheKey) => {
      lambdaTryOnImageUrlCache.set(candidateCacheKey, resolvedCacheEntry);
    });
    return cachedImageDataUrl;
  }

  const cachedImageUrl = cacheKeys
    .map((candidateCacheKey) => lambdaTryOnImageUrlCache.get(candidateCacheKey))
    .find((candidate): candidate is Promise<string> => Boolean(candidate));
  if (cachedImageUrl) {
    return cachedImageUrl;
  }

  const request = (async () => {
    try {
      const response = await fetch(imageUrl, {
        method: 'GET',
        cache: 'force-cache',
        credentials: 'omit',
      });

      if (!response.ok) {
        return imageUrl;
      }

      const imageBlob = await response.blob();
      const imageDataUrl = sanitizeTryOnImageUrl(await convertBlobToDataUrl(imageBlob));
      if (!imageDataUrl) {
        return imageUrl;
      }

      // Oversized entries stay in the in-memory cache only, and the image data
      // is stored once with alias keys pointing at it instead of duplicating
      // the same payload under every cache key.
      persistLambdaTryOnImageData(cacheKeys, imageDataUrl);

      return imageDataUrl;
    } catch {
      return imageUrl;
    }
  })();

  cacheKeys.forEach((candidateCacheKey) => {
    lambdaTryOnImageUrlCache.set(candidateCacheKey, request);
  });

  void request.then((resolvedImageUrl) => {
    if (resolvedImageUrl !== imageUrl) {
      return;
    }

    // A remote URL means the byte download failed. Do not retain that expiring
    // URL as a cache hit; a later try_on_results refresh must be able to retry
    // with a newly issued URL.
    cacheKeys.forEach((candidateCacheKey) => {
      if (lambdaTryOnImageUrlCache.get(candidateCacheKey) === request) {
        lambdaTryOnImageUrlCache.delete(candidateCacheKey);
      }
    });
  });
  return request;
}

function resolveLambdaTryOnImageUrlForDisplay(
  imageUrlInput: unknown,
  cacheKeyInput?: unknown,
  additionalCacheKeyInputs: unknown[] = []
) {
  const imageUrl = sanitizeTryOnImageUrl(imageUrlInput);
  if (!imageUrl || imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  const cacheKeys = getLambdaTryOnImageCacheKeys(
    imageUrl,
    cacheKeyInput,
    additionalCacheKeyInputs
  );
  const cachedImageDataUrl = cacheKeys
    .map((candidateCacheKey) => readCachedLambdaTryOnImageData(candidateCacheKey))
    .find(Boolean);
  if (cachedImageDataUrl) {
    cacheKeys.forEach((candidateCacheKey) => {
      lambdaTryOnStableDisplayUrlCache.set(candidateCacheKey, cachedImageDataUrl);
    });
    return cachedImageDataUrl;
  }

  const stableDisplayUrl = cacheKeys
    .map((candidateCacheKey) => lambdaTryOnStableDisplayUrlCache.get(candidateCacheKey))
    .find(Boolean);
  if (
    stableDisplayUrl &&
    !shouldReplaceStableTryOnImageUrl(stableDisplayUrl, imageUrl)
  ) {
    return stableDisplayUrl;
  }

  if (stableDisplayUrl) {
    cacheKeys.forEach((candidateCacheKey) => {
      if (lambdaTryOnStableDisplayUrlCache.get(candidateCacheKey) === stableDisplayUrl) {
        lambdaTryOnStableDisplayUrlCache.delete(candidateCacheKey);
      }
    });
  }

  // The API may issue a fresh presigned URL for the same result on every poll.
  // Keep the first URL stable so React does not replace an in-flight <img> src
  // and Firefox does not cancel it with NS_BINDING_ABORTED.
  cacheKeys.forEach((candidateCacheKey) => {
    lambdaTryOnStableDisplayUrlCache.set(candidateCacheKey, imageUrl);
  });

  // A successful try_on_results response must be able to update the UI even
  // when the separate CDN image download is slow or blocked by CORS. Cache the
  // bytes opportunistically, but render the sanitized URL immediately.
  void cacheLambdaTryOnImageUrl(
    imageUrl,
    cacheKeyInput,
    additionalCacheKeyInputs
  );
  return imageUrl;
}

function buildInventoryIdSetKey(inventoryIds: string[]) {
  return sanitizeInventoryIds(inventoryIds).sort().join('|');
}

function buildLambdaTryOnResultsCacheKey(rawDataId: string, activeInventoryIds: string[]) {
  const activeInventoryKey = buildInventoryIdSetKey(activeInventoryIds);
  return activeInventoryKey ? `${rawDataId}:${activeInventoryKey}` : rawDataId;
}

function getPrimaryInventoryId(inventoryIds: string[] | undefined) {
  return sanitizeInventoryIds(inventoryIds)
    .sort((leftInventoryId, rightInventoryId) => leftInventoryId.localeCompare(rightInventoryId))[0] ?? '';
}

function filterSourceSelectionsByInventoryIds(
  sourceSelections: LambdaTryOnSourceSelection[],
  relevantInventoryIds: string[]
) {
  const normalizedRelevantInventoryIds = sanitizeInventoryIds(relevantInventoryIds);
  if (normalizedRelevantInventoryIds.length <= 0) {
    return sourceSelections;
  }

  const relevantInventoryIdSet = new Set(normalizedRelevantInventoryIds);
  const filteredSelections = sourceSelections.filter((selection) => {
    const inventoryId = getPrimaryInventoryId(selection.inventoryIds);
    return inventoryId ? relevantInventoryIdSet.has(inventoryId) : false;
  });

  return filteredSelections.length > 0 ? filteredSelections : sourceSelections;
}

function buildSelectionPayload(
  sourceSelections: LambdaTryOnSourceSelection[],
  primaryInventoryId: string,
  fallbackInventoryIds: string[]
) {
  const candidateIds = sourceSelections
    .map((selection) => getPrimaryInventoryId(selection.inventoryIds))
    .filter(Boolean);
  const inventoryId = primaryInventoryId || candidateIds[0] || fallbackInventoryIds[0] || null;

  return {
    inventoryId,
    inventoryIds: sanitizeInventoryIds([
      ...(inventoryId ? [inventoryId] : []),
      ...candidateIds,
      ...fallbackInventoryIds,
    ]),
  };
}

export function createTryOnIdempotencyKey() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `tryon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function createTryOnRequestHeaders(headers?: HeadersInit) {
  return createUserSessionHeaders(headers, { ensureGuestSession: true });
}

function isGuestTryOnRequest(headers: Headers) {
  return !headers.has('Authorization');
}

function getTryOnStoreName() {
  return typeof __STORE_NAME__ === 'string' ? __STORE_NAME__.trim() : '';
}

export async function submitLambdaTryOnRemoveItem(input: SubmitLambdaTryOnRemoveItemInput) {
  const inventoryId = sanitizeUuid(input.inventoryId);
  if (!inventoryId) {
    throw new Error('Unable to remove try-on item: missing inventory_id.');
  }
  const headers = createTryOnRequestHeaders({ 'Content-Type': 'application/json' });
  const response = await fetchAwsApi('/api/enqueue_tasks', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      queue: 'try_on_remove_item',
      store: getTryOnStoreName(),
      ...(isGuestTryOnRequest(headers) ? { session_id: headers.get('X-Session-ID') } : {}),
      inventory_id: inventoryId,
    }),
  });
  const responseBody = await parseApiResponse(response);
  if (!response.ok) {
    throw new Error(getApiErrorMessage(responseBody, 'Unable to remove try-on item.'));
  }
  return responseBody;
}

export async function submitLambdaTryOnAddItem(input: SubmitLambdaTryOnAddItemInput) {
  const rawDataId = sanitizeUuid(input.rawDataId);
  if (!rawDataId) {
    throw new Error('Unable to start try-on generation: missing raw_data_id.');
  }

  const primaryInventoryId = sanitizeUuid(input.primaryInventoryId);
  const fallbackInventoryIds = sanitizeInventoryIds(input.fallbackInventoryIds);
  const sourceSelections = filterSourceSelectionsByInventoryIds(
    input.sourceSelections ?? [],
    [
      ...(primaryInventoryId ? [primaryInventoryId] : []),
      ...fallbackInventoryIds,
    ]
  );
  const selectionPayload = buildSelectionPayload(
    sourceSelections,
    primaryInventoryId || fallbackInventoryIds[0] || '',
    fallbackInventoryIds
  );
  const idempotencyKey = sanitizeGenericText(
    input.idempotencyKey || createTryOnIdempotencyKey(),
    160
  );
  const requestHeaders = createTryOnRequestHeaders({
    'Content-Type': 'application/json',
    'X-Idempotency-Key': idempotencyKey,
  });
  const isGuestRequest = isGuestTryOnRequest(requestHeaders);
  if (isGuestRequest && getGuestImageGenerationLimitState().isLimitReached) {
    throw new Error('Guest try-on limit reached. Please sign in to continue.');
  }
  const shouldEnqueueGeneration = input.confirmGeneration === true || isGuestRequest;
  if (!shouldEnqueueGeneration) {
    await refreshAccountCredits();
  }
  const requestBody = {
    store: getTryOnStoreName(),
    ...(isGuestRequest ? { session_id: requestHeaders.get('X-Session-ID') } : {}),
    inventory_id: selectionPayload.inventoryId,
    idempotency_key: idempotencyKey,
    confirm_generation: shouldEnqueueGeneration,
    ...(isGuestRequest
      ? {
          turnstileRequired: input.turnstileRequired === true,
          ...(input.turnstileRequired ? { turnstileToken: input.turnstileToken ?? '' } : {}),
        }
      : {}),
  };

  const response = await fetchAwsApi(
    shouldEnqueueGeneration ? '/api/enqueue_tasks' : '/api/try_on_credit_check',
    {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify({
        ...(shouldEnqueueGeneration ? { queue: 'try_on_add_item' } : {}),
        ...requestBody,
      }),
    }
  );
  const responseBody = await parseApiResponse(response);

  const errorMessage = getApiErrorMessage(responseBody, 'Unable to start try-on generation.');
  if (!isApiResponseOk(response, responseBody) || isTryOnProductLimitError(errorMessage)) {
    throw new Error(errorMessage);
  }
  invalidateLambdaTryOnResultsCache(rawDataId);

  const selectedResult = (
    responseBody &&
    typeof responseBody === 'object' &&
    !Array.isArray(responseBody) &&
    typeof (responseBody as { selected_result?: unknown }).selected_result === 'object'
  )
    ? (responseBody as { selected_result?: Record<string, unknown> }).selected_result ?? {}
    : {};
  const resultInventoryIds = sanitizeTryOnResultInventoryIds(selectedResult);
  const resolvedResultInventoryIds = resultInventoryIds.length > 0
    ? resultInventoryIds
    : selectionPayload.inventoryIds;
  const selectedResultId = sanitizeTryOnResultId(selectedResult);
  const generationRequestId = sanitizeTryOnGenerationRequestId(selectedResult);
  const responseRecord = responseBody && typeof responseBody === 'object' && !Array.isArray(responseBody)
    ? responseBody as Record<string, unknown>
    : null;
  const requiresGeneration = responseRecord
    ? sanitizeBoolean(
        responseRecord.requires_generation ??
        responseRecord.requiresGeneration ??
        responseRecord.requires_confirmation ??
        responseRecord.requiresConfirmation
      )
    : false;
  const hasRequiresConfirmationField = Boolean(
    responseRecord &&
    (
      Object.prototype.hasOwnProperty.call(responseRecord, 'requires_confirmation') ||
      Object.prototype.hasOwnProperty.call(responseRecord, 'requiresConfirmation')
    )
  );
  const requiresConfirmation = hasRequiresConfirmationField
    ? sanitizeBoolean(responseRecord?.requires_confirmation ?? responseRecord?.requiresConfirmation)
    : requiresGeneration;
  const creditCost = sanitizeCreditCost(
    responseRecord?.credit_cost ?? responseRecord?.creditCost
  );

  return {
    rawDataId,
    resultId: selectedResultId,
    generationRequestId,
    status: sanitizeTryOnResultStatus(selectedResult),
    imageUrl: resolveLambdaTryOnImageUrlForDisplay(
      getTryOnResultImageUrl(selectedResult),
      selectedResultId ? `raw:${rawDataId}:result:${selectedResultId}` : rawDataId,
      [getLambdaTryOnInventoryImageCacheKey(rawDataId, resolvedResultInventoryIds)]
    ),
    inventoryId: selectionPayload.inventoryId,
    inventoryIds: resolvedResultInventoryIds,
    idempotencyKey,
    requiresGeneration,
    requiresConfirmation,
    creditCost,
  } satisfies SubmitLambdaTryOnAddItemResult;
}

/** Identifies the API response used when the try-on tray already has six products. */
export function isTryOnProductLimitError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /^You can select up to 6 products total\.?$/i.test(message.trim());
}

export async function saveLambdaTryOnResult(resultIdInput: string) {
  const resultId = sanitizeGenericText(String(resultIdInput ?? ''), 160);
  if (!resultId) {
    throw new Error('Unable to save try-on result: missing result_id.');
  }

  const requestHeaders = createTryOnRequestHeaders({
    'Content-Type': 'application/json',
  });

  const response = await fetchAwsApi('/api/try_on_save_result', {
    method: 'POST',
    headers: requestHeaders,
    body: JSON.stringify({
      result_id: resultId,
    }),
  });
  const responseBody = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(responseBody, 'Unable to save try-on result.'));
  }

  const result = (
    responseBody &&
    typeof responseBody === 'object' &&
    !Array.isArray(responseBody) &&
    typeof (responseBody as { result?: unknown }).result === 'object'
  )
    ? (responseBody as { result?: Record<string, unknown> }).result ?? {}
    : {};
  const rawDataId = sanitizeGenericText(String(result.raw_data_id ?? result.rawDataId ?? ''), 120);
  if (rawDataId) {
    invalidateLambdaTryOnResultsCache(rawDataId);
  }

  return {
    id: sanitizeGenericText(String(result.id ?? resultId), 160) || resultId,
    rawDataId,
    s3Key: sanitizeGenericText(String(result.s3_key ?? result.s3Key ?? ''), 512),
    isSaved:
      result.is_saved === true ||
      result.isSaved === true ||
      String(result.is_saved ?? result.isSaved ?? '').toLowerCase() === 'true',
    savedAt: sanitizeGenericText(String(result.saved_at ?? result.savedAt ?? ''), 80),
  } satisfies SaveLambdaTryOnResultResult;
}

async function fetchLambdaTryOnResultsRequest(
  rawDataIdInput: string,
  options: {
    activeInventoryIds?: string[];
    forceRefresh?: boolean;
    minRefreshIntervalMs?: number;
  } = {}
) {
  const rawDataId = sanitizeUuid(rawDataIdInput);
  if (!rawDataId) {
    throw new Error('Unable to check try-on results: missing raw_data_id.');
  }

  const activeInventoryIds = sanitizeInventoryIds(options.activeInventoryIds);
  const requestCacheKey = buildLambdaTryOnResultsCacheKey(rawDataId, activeInventoryIds);
  const cachedResult = lambdaTryOnResultsCache.get(requestCacheKey);
  const cachedResultUpdatedAt = lambdaTryOnResultsCacheUpdatedAt.get(requestCacheKey) ?? 0;
  const minRefreshIntervalMs = Math.max(0, Math.trunc(options.minRefreshIntervalMs ?? 0));
  if (
    cachedResult &&
    (
      !options.forceRefresh ||
      (
        minRefreshIntervalMs > 0 &&
        Date.now() - cachedResultUpdatedAt < minRefreshIntervalMs
      )
    )
  ) {
    return cachedResult;
  }

  const cachedRequest = lambdaTryOnResultsRequestCache.get(requestCacheKey);
  if (cachedRequest) {
    return cachedRequest;
  }

  const requestCacheVersion = lambdaTryOnResultsCacheVersions.get(rawDataId) ?? 0;
  const request = (async () => {
    const requestHeaders = createTryOnRequestHeaders();
    const queryParams = new URLSearchParams({ raw_data_id: rawDataId });
    queryParams.set('store', getTryOnStoreName());
    if (isGuestTryOnRequest(requestHeaders)) {
      queryParams.set('account_id', 'null');
    }
    const requestPath = `/api/try_on_results?${queryParams.toString()}`;

    const response = await fetchAwsApi(requestPath, {
      method: 'GET',
      headers: requestHeaders,
    });
    const responseBody = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(getApiErrorMessage(responseBody, 'Unable to check try-on status.'));
    }

    const results = Array.isArray((responseBody as { results?: unknown }).results)
      ? await Promise.all(
          ((responseBody as { results?: unknown[] }).results ?? []).map(async (result) => {
            if (!result || typeof result !== 'object' || Array.isArray(result)) {
              return null;
            }

            const candidate = result as Record<string, unknown>;
            const resultId = sanitizeTryOnResultId(candidate);
            const objectKey = sanitizeGenericText(
              String(candidate.s3_key ?? candidate.s3Key ?? candidate.objectKey ?? candidate.object_key ?? ''),
              512
            ).replace(/^\/+/, '');
            const inventoryIds = sanitizeTryOnResultInventoryIds(candidate);
            const freshImageUrl = sanitizeTryOnImageUrl(getTryOnResultImageUrl(candidate));
            const parsedResult: LambdaTryOnResultRecord = {
              id: resultId,
              generationRequestId: sanitizeTryOnGenerationRequestId(candidate) || undefined,
              status: sanitizeTryOnResultStatus(candidate),
              imageUrl: resolveLambdaTryOnImageUrlForDisplay(
                freshImageUrl,
                resultId ? `raw:${rawDataId}:result:${resultId}` : rawDataId,
                [getLambdaTryOnInventoryImageCacheKey(rawDataId, inventoryIds)]
              ),
              freshImageUrl: freshImageUrl || undefined,
              objectKey: objectKey || undefined,
              inventoryIds,
              errorMessage: sanitizeGenericText(String(candidate.error_message ?? ''), 240),
              isSaved:
                candidate.is_saved === true ||
                candidate.isSaved === true ||
                String(candidate.is_saved ?? candidate.isSaved ?? '').toLowerCase() === 'true',
              createdAt: sanitizeGenericText(String(candidate.created_at ?? candidate.createdAt ?? ''), 80) || undefined,
              completedAt: sanitizeGenericText(String(candidate.completed_at ?? candidate.completedAt ?? ''), 80) || undefined,
            };
            return parsedResult;
          })
        ).then((parsedResults) => parsedResults.filter((result): result is LambdaTryOnResultRecord => result !== null))
      : [];

    const payload = {
      rawDataId,
      results,
    } satisfies FetchLambdaTryOnResultsResult;

    if ((lambdaTryOnResultsCacheVersions.get(rawDataId) ?? 0) === requestCacheVersion) {
      lambdaTryOnResultsCache.set(requestCacheKey, payload);
      lambdaTryOnResultsCacheUpdatedAt.set(requestCacheKey, Date.now());
    }
    return payload;
  })();

  lambdaTryOnResultsRequestCache.set(requestCacheKey, request);

  try {
    return await request;
  } finally {
    const activeRequest = lambdaTryOnResultsRequestCache.get(requestCacheKey);
    if (activeRequest === request) {
      lambdaTryOnResultsRequestCache.delete(requestCacheKey);
    }
  }
}

export function fetchLambdaTryOnResults(
  rawDataIdInput: string,
  options: {
    activeInventoryIds?: string[];
    forceRefresh?: boolean;
    minRefreshIntervalMs?: number;
  } = {}
) {
  return fetchLambdaTryOnResultsRequest(rawDataIdInput, options);
}

export function findMatchingLambdaTryOnResult(
  payload: FetchLambdaTryOnResultsResult,
  options: {
    resultId?: string;
    objectKey?: string;
    inventoryIds?: string[];
    savedOnly?: boolean;
  } = {}
) {
  const candidateResults = options.savedOnly
    ? payload.results.filter((result) => result.isSaved)
    : payload.results;
  const requestedResultId = sanitizeGenericText(String(options.resultId ?? ''), 120);
  if (requestedResultId) {
    const directMatch = candidateResults.find(
      (result) => result.id === requestedResultId || result.generationRequestId === requestedResultId
    );
    if (directMatch) {
      return directMatch;
    }
  }

  const requestedObjectKey = sanitizeGenericText(String(options.objectKey ?? ''), 512);
  if (requestedObjectKey) {
    const objectKeyMatch = candidateResults.find(
      (result) => sanitizeGenericText(String(result.objectKey ?? ''), 512) === requestedObjectKey
    );
    if (objectKeyMatch) {
      return objectKeyMatch;
    }
  }

  const requestedInventoryIds = sanitizeInventoryIds(options.inventoryIds);
  const requestedInventoryKey = buildInventoryIdSetKey(requestedInventoryIds);
  if (requestedInventoryKey) {
    const inventoryMatch = candidateResults.find(
      (result) => buildInventoryIdSetKey(result.inventoryIds) === requestedInventoryKey
    );
    if (inventoryMatch) {
      return inventoryMatch;
    }

    return null;
  }

  if (requestedResultId || requestedObjectKey) {
    return null;
  }

  return candidateResults[0] ?? null;
}

export interface SavedLambdaTryOnImageLookup {
  rawDataId?: string;
  resultId?: string;
  objectKey?: string;
  inventoryIds?: string[];
}

export async function loadFreshLambdaTryOnImageUrl(input: SavedLambdaTryOnImageLookup) {
  const rawDataId = sanitizeUuid(input.rawDataId);
  if (!rawDataId) {
    return '';
  }

  const payload = await fetchLambdaTryOnResults(rawDataId, { forceRefresh: true });
  const matchingResult = findMatchingLambdaTryOnResult(payload, input);
  return sanitizeTryOnImageUrl(matchingResult?.freshImageUrl ?? matchingResult?.imageUrl);
}

function getSavedLambdaTryOnImageCacheKeys(input: SavedLambdaTryOnImageLookup) {
  const rawDataId = sanitizeUuid(input.rawDataId);
  if (!rawDataId) {
    return [];
  }

  const resultId = sanitizeGenericText(String(input.resultId ?? ''), 120);
  const inventoryCacheKey = getLambdaTryOnInventoryImageCacheKey(
    rawDataId,
    sanitizeInventoryIds(input.inventoryIds)
  );

  return [
    resultId ? `raw:${rawDataId}:result:${resultId}` : '',
    inventoryCacheKey,
  ].filter(Boolean);
}

export function readCachedSavedLambdaTryOnImage(input: SavedLambdaTryOnImageLookup) {
  return getSavedLambdaTryOnImageCacheKeys(input)
    .map((cacheKey) => readCachedLambdaTryOnImageData(cacheKey))
    .find((imageUrl) => imageUrl.startsWith('data:image/') || imageUrl.startsWith('blob:')) ?? '';
}

export async function loadCachedOrFreshSavedLambdaTryOnImage(
  input: SavedLambdaTryOnImageLookup
) {
  const cachedImageUrl = readCachedSavedLambdaTryOnImage(input);
  if (cachedImageUrl) {
    return cachedImageUrl;
  }

  const cachedImageRequest = getSavedLambdaTryOnImageCacheKeys(input)
    .map((cacheKey) => lambdaTryOnImageUrlCache.get(cacheKey))
    .find((request): request is Promise<string> => Boolean(request));
  if (cachedImageRequest) {
    const memoryCachedImageUrl = await cachedImageRequest;
    if (
      memoryCachedImageUrl.startsWith('data:image/') ||
      memoryCachedImageUrl.startsWith('blob:')
    ) {
      return memoryCachedImageUrl;
    }
  }

  const rawDataId = sanitizeUuid(input.rawDataId);
  if (!rawDataId) {
    return '';
  }

  const payload = await fetchLambdaTryOnResults(rawDataId, { forceRefresh: true });
  const matchingResult = findMatchingLambdaTryOnResult(payload, {
    resultId: input.resultId,
    objectKey: input.objectKey,
    inventoryIds: input.inventoryIds,
  });
  const freshImageUrl = sanitizeTryOnImageUrl(
    matchingResult?.freshImageUrl ?? matchingResult?.imageUrl
  );
  if (!matchingResult || !freshImageUrl) {
    return '';
  }

  const cacheKeys = getSavedLambdaTryOnImageCacheKeys({
    rawDataId,
    resultId: matchingResult.id || input.resultId,
    inventoryIds: matchingResult.inventoryIds.length > 0
      ? matchingResult.inventoryIds
      : input.inventoryIds,
  });
  const [primaryCacheKey, ...aliasCacheKeys] = cacheKeys;
  const refreshedImageUrl = await cacheLambdaTryOnImageUrl(
    freshImageUrl,
    primaryCacheKey || rawDataId,
    aliasCacheKeys
  );
  return refreshedImageUrl.startsWith('data:image/') || refreshedImageUrl.startsWith('blob:')
    ? refreshedImageUrl
    : '';
}

export async function loadLambdaTryOnResultForInventoryIds(
  rawDataIdInput: string,
  inventoryIdsInput: string[],
  options: {
    activeInventoryIds?: string[];
    forceRefresh?: boolean;
    minRefreshIntervalMs?: number;
    savedOnly?: boolean;
    requireResultId?: boolean;
  } = {}
) {
  const rawDataId = sanitizeUuid(rawDataIdInput);
  const inventoryIds = sanitizeInventoryIds(inventoryIdsInput);
  if (!rawDataId || inventoryIds.length <= 0) {
    return null;
  }

  const inventoryImageCacheKey = getLambdaTryOnInventoryImageCacheKey(rawDataId, inventoryIds);
  const cachedImageUrl = inventoryImageCacheKey
    ? readCachedLambdaTryOnImageData(inventoryImageCacheKey)
    : '';
  if (cachedImageUrl && !options.savedOnly && !options.requireResultId) {
    return {
      id: '',
      status: 'succeeded',
      imageUrl: cachedImageUrl,
      objectKey: undefined,
      inventoryIds,
      errorMessage: '',
      isSaved: false,
    } satisfies LambdaTryOnResultRecord;
  }

  const activeInventoryIds = sanitizeInventoryIds(options.activeInventoryIds);
  const cachedPayload = lambdaTryOnResultsCache.get(buildLambdaTryOnResultsCacheKey(rawDataId, activeInventoryIds));
  const cachedMatch = cachedPayload
    ? findMatchingLambdaTryOnResult(cachedPayload, { inventoryIds, savedOnly: options.savedOnly })
    : null;
  if (cachedMatch?.imageUrl && (!options.requireResultId || Boolean(cachedMatch.id))) {
    return cachedMatch;
  }

  const responseBody = await fetchLambdaTryOnResults(rawDataId, {
    ...options,
    forceRefresh: options.forceRefresh ?? true,
  });
  const matchingResult = findMatchingLambdaTryOnResult(responseBody, {
    inventoryIds,
    savedOnly: options.savedOnly,
  });
  if (options.requireResultId && !matchingResult?.id) {
    return null;
  }
  return matchingResult;
}

export async function loadLambdaTryOnResultForVideoSource(
  rawDataIdInput: string,
  options: {
    resultId?: string;
    objectKey?: string;
    inventoryIds?: string[];
  } = {}
) {
  const rawDataId = sanitizeUuid(rawDataIdInput);
  if (!rawDataId) {
    return null;
  }

  const inventoryIds = sanitizeInventoryIds(options.inventoryIds);
  const responseBody = await fetchLambdaTryOnResults(rawDataId, {
    activeInventoryIds: inventoryIds,
    forceRefresh: true,
  });

  return findMatchingLambdaTryOnResult(responseBody, {
    resultId: options.resultId,
    objectKey: options.objectKey,
    inventoryIds,
  });
}

export function isPendingLambdaTryOnStatus(status: string) {
  return (
    !status ||
    status === 'queued' ||
    status === 'pending' ||
    status === 'generating' ||
    status === 'processing' ||
    status === 'running' ||
    status === 'created'
  );
}

export function isSuccessfulLambdaTryOnStatus(status: string) {
  return status === 'succeeded' || status === 'success' || status === 'completed' || status === 'complete';
}

export function isFailedLambdaTryOnStatus(status: string) {
  return status === 'failed' || status === 'error' || status === 'cancelled' || status === 'canceled';
}
